import React, { useState, useEffect } from 'react';
import {
  FaCheck, FaWhatsapp
} from 'react-icons/fa';
import {
  AlertCircle, Loader2
} from 'lucide-react';
import {
  QRCodeSVG
} from 'qrcode.react';

interface BaileysConnectionPanelProps {
  connectionStatus: string;
  qrCode: string;
  saveError?: string;
  hideAssistant?: boolean;
  pairingCode?: string;
}

const BaileysConnectionPanel: React.FC<BaileysConnectionPanelProps> = ({
  connectionStatus,
  qrCode,
  saveError,
  hideAssistant = false,
  pairingCode,
}) => {
  // Elapsed timer while waiting to scan, so the panel visibly moves instead of sitting on
  // a dead "Awaiting Scan". Resets whenever we leave the pending state.
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (connectionStatus !== 'pending') { setElapsed(0); return; }
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [connectionStatus]);

  const isPhoneMethod = !!pairingCode || connectionStatus === 'pairing';
  const howToSteps = isPhoneMethod
    ? [
        {
          step: 1,
          text: hideAssistant
            ? 'Fill in the Instance Name and phone number, then click Submit'
            : 'Fill in the Instance Name, Assistant and phone number, then click Submit',
        },
        { step: 2, text: 'Open WhatsApp on your phone' },
        { step: 3, text: 'Go to Settings → Linked Devices' },
        { step: 4, text: 'Tap "Link with phone number" and enter the code' },
      ]
    : [
        {
          step: 1,
          text: hideAssistant
            ? 'Fill in the Instance Name, then click Submit'
            : 'Fill in the Instance Name and Assistant, then click Submit',
        },
        { step: 2, text: 'Open WhatsApp on your phone' },
        { step: 3, text: 'Go to Settings → Linked Devices' },
        { step: 4, text: 'Tap "Link a Device" and scan the QR code' },
      ];
  if (connectionStatus === '') {
    return (
        <div className="w-full text-left space-y-5">
            <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 text-primary rounded-xl flex items-center justify-center shrink-0">
                    <FaWhatsapp size={20} />
                </div>
                <div>
                    <h4 className="text-sm font-bold text-foreground">How to connect</h4>
                    <p className="text-[11px] text-muted-foreground">Follow the steps below to link your WhatsApp account.</p>
                </div>
            </div>
            <ol className="space-y-3">
                {howToSteps.map(({
            step, text
          }) => (
              <li key={step} className="flex items-start gap-3">
                  <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                      {step}
                  </span>
                  <span className="text-xs text-muted-foreground leading-relaxed">{text}</span>
              </li>
          ))}
            </ol>
        </div>
    );
  }

  if (connectionStatus === 'pending') {
    // Soft progress: we can't know the exact moment WhatsApp links, so ease toward 92%
    // over ~45s (the usual window) and let the connected state finish it. Keeps the bar
    // clearly moving without faking completion.
    const pct = Math.min(92, Math.round((elapsed / 45) * 92));
    const mm = Math.floor(elapsed / 60);
    const ss = String(elapsed % 60).padStart(2, '0');
    const steps = [
      { label: 'QR code ready', done: !!qrCode, active: !qrCode },
      { label: 'Scan it on your phone', done: false, active: !!qrCode },
      { label: 'Linking your account', done: false, active: false },
    ];
    return (
        <>
            <div className="relative p-1 bg-card rounded-2xl shadow-2xl border border-border">
                <div className="p-3 bg-muted rounded-xl flex items-center justify-center min-w-[200px] min-h-[200px]">
                    {qrCode ? (
                        <div className="animate-in zoom-in-95 duration-500 bg-white p-2 rounded-lg">
                            {qrCode.startsWith('data:') ? (
                                <img src={qrCode} alt="WhatsApp QR Code" className="w-[180px] h-[180px]" />
                ) : (
                    <QRCodeSVG value={qrCode} size={180} />
                )}
                        </div>
            ) : (
                <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-10 h-10 text-primary animate-spin" />
                    <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Generating</span>
                </div>
            )}
                </div>
            </div>
            <div className="w-full max-w-[280px] space-y-3">
                <div className="flex items-center justify-between">
                    <div className="inline-flex items-center gap-2">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500" />
                        </span>
                        <span className="text-[11px] font-semibold text-foreground">Waiting for you to scan</span>
                    </div>
                    <span className="text-[11px] tabular-nums text-muted-foreground">{mm}:{ss}</span>
                </div>
                <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                    <div className="h-full rounded-full bg-primary transition-all duration-1000 ease-out" style={{ width: `${pct}%` }} />
                </div>
                <ol className="space-y-1.5 text-left">
                    {steps.map((s, i) => (
                        <li key={i} className="flex items-center gap-2">
                            <span className={`flex items-center justify-center w-4 h-4 rounded-full text-[9px] font-bold shrink-0 ${s.done ? 'bg-green-500 text-white' : s.active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground border border-border'}`}>
                                {s.done ? '✓' : i + 1}
                            </span>
                            <span className={`text-[11px] ${s.active ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>{s.label}</span>
                            {s.active && <Loader2 className="w-3 h-3 text-primary animate-spin ml-auto" />}
                        </li>
                    ))}
                </ol>
                <p className="text-[10px] text-muted-foreground leading-snug text-left">
                    {elapsed >= 40
                        ? 'Taking a while? Make sure your phone has internet, then keep this open.'
                        : 'Open WhatsApp, go to Linked Devices, and scan the code. Keep this open while it connects.'}
                </p>
            </div>
        </>
    );
  }

  if (connectionStatus === 'pairing') {
    const formatted = pairingCode
      ? `${pairingCode.slice(0, 4)}-${pairingCode.slice(4)}`
      : '...';
    return (
        <>
            <div className="relative p-1 bg-card rounded-2xl shadow-2xl border border-border">
                <div className="p-6 bg-muted rounded-xl flex items-center justify-center min-w-[200px] min-h-[120px]">
                    {pairingCode ? (
                        <div className="animate-in zoom-in-95 duration-500 text-center">
                            <span className="font-mono text-4xl font-bold tracking-[0.2em] text-foreground">
                                {formatted}
                            </span>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center gap-3">
                            <Loader2 className="w-10 h-10 text-primary animate-spin" />
                            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Generating</span>
                        </div>
                    )}
                </div>
            </div>
            <div className="space-y-3">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-500/10 rounded-full border border-amber-500/20">
                    <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                    <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wider">Awaiting Entry</span>
                </div>
                <h4 className="text-base font-bold text-foreground leading-tight">Enter Code in WhatsApp</h4>
                <p className="text-xs text-muted-foreground max-w-[260px] mx-auto">
                    Open WhatsApp → Settings → Linked Devices → <strong>Link with phone number</strong> and enter the code above.
                </p>
                <p className="text-[10px] text-muted-foreground">Code expires in ~60 seconds.</p>
            </div>
        </>
    );
  }

  if (connectionStatus === 'saving') {
    return (
        <>
            <div className="w-20 h-20 bg-primary/10 text-primary rounded-full flex items-center justify-center mx-auto border-4 border-border">
                <Loader2 size={36} className="animate-spin" />
            </div>
            <div className="space-y-2">
                <h4 className="text-xl font-bold text-foreground">Saving Channel...</h4>
                <p className="text-sm text-muted-foreground px-4">
                    WhatsApp connected. Registering channel, please wait.
                </p>
            </div>
        </>
    );
  }

  if (connectionStatus === 'save_failed') {
    return (
        <>
            <div className="w-20 h-20 bg-red-500/10 text-red-500 rounded-full flex items-center justify-center mx-auto border-4 border-border">
                <AlertCircle size={36} />
            </div>
            <div className="space-y-2">
                <h4 className="text-xl font-bold text-foreground">Channel Not Created</h4>
                <p className="text-sm text-red-500 px-4">
                    {saveError || 'Failed to save channel. Please close and try again.'}
                </p>
            </div>
        </>
    );
  }

  if (connectionStatus === 'connected') {
    return (
        <>
            <div className="w-20 h-20 bg-green-500/10 text-green-500 rounded-full flex items-center justify-center mx-auto border-4 border-border">
                <FaCheck size={36} />
            </div>
            <div className="space-y-2">
                <h4 className="text-xl font-bold text-foreground">Instance Active</h4>
                <p className="text-sm text-muted-foreground px-4">
                    Your enterprise account is successfully linked and routing traffic to your AI Assistant.
                </p>
            </div>
        </>
    );
  }

  if (connectionStatus === 'reauth_required' || connectionStatus === 'disconnected') {
    return (
        <>
            <div className="w-20 h-20 bg-red-500/10 text-red-500 rounded-full flex items-center justify-center mx-auto border-4 border-border">
                <AlertCircle size={36} />
            </div>
            <div className="space-y-2">
                <h4 className="text-xl font-bold text-foreground">
                    {connectionStatus === 'reauth_required' ? 'Session Expired' : 'Disconnected'}
                </h4>
                <p className="text-sm text-muted-foreground px-4">
                    The WhatsApp link didn’t complete. Close this dialog and start the connection again.
                </p>
            </div>
        </>
    );
  }

  if (connectionStatus === 'reconnecting') {
    return (
        <>
            <div className="w-20 h-20 bg-amber-500/10 text-amber-500 rounded-full flex items-center justify-center mx-auto border-4 border-border">
                <Loader2 size={36} className="animate-spin" />
            </div>
            <div className="space-y-2">
                <h4 className="text-xl font-bold text-foreground">Reconnecting…</h4>
                <p className="text-sm text-muted-foreground px-4">
                    The connection is unstable. Waiting for it to recover — this can take a moment.
                </p>
            </div>
        </>
    );
  }

  // Never default an UNRECOGNIZED status to a green "connected" — show a neutral, honest
  // waiting state so a dead/unknown session can't masquerade as linked.
  return (
      <>
          <div className="w-20 h-20 bg-muted text-muted-foreground rounded-full flex items-center justify-center mx-auto border-4 border-border">
              <Loader2 size={36} className="animate-spin" />
          </div>
          <div className="space-y-2">
              <h4 className="text-xl font-bold text-foreground">Connecting…</h4>
              <p className="text-sm text-muted-foreground px-4">
                  Waiting for WhatsApp to confirm the connection.
              </p>
          </div>
      </>
  );
};

export default BaileysConnectionPanel;
